import React, { useState, useEffect, useMemo } from "react";
import { useAuth } from "../contexts/AuthContext";
import { supabase } from "../utils/supabase";
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  XCircleIcon,
  AcademicCapIcon,
} from "@heroicons/react/24/outline";
import { format, parseISO, addMonths, differenceInDays, isAfter } from "date-fns";
import { nl } from "date-fns/locale";
import toast from "react-hot-toast";
import ConfirmDialog from "../components/ConfirmDialog";

// ─── Types ───────────────────────────────────────────────────────────────────

interface TrainingType {
  id: string;
  pharmacy_id: string;
  name: string;
  description?: string;
  validity_months: number;
  is_mandatory: boolean;
  created_at: string;
}

interface TrainingRecord {
  id: string;
  pharmacy_id: string;
  training_type_id: string;
  training_type_name: string;
  user_id?: string;
  user_name: string;
  completed_date: string;
  expiry_date?: string;
  trainer?: string;
  notes?: string;
  created_at: string;
}

interface PharmacyUser {
  id: string;
  full_name: string;
  role: string;
}

type CellStatus = "valid" | "expiring" | "expired" | "none";

// ─── Helpers ─────────────────────────────────────────────────────────────────

const cellStatus = (record: TrainingRecord | undefined): CellStatus => {
  if (!record) return "none";
  const expiry = record.expiry_date ? parseISO(record.expiry_date) : null;
  if (!expiry) return "valid";
  const today = new Date();
  if (!isAfter(expiry, today)) return "expired";
  if (differenceInDays(expiry, today) <= 60) return "expiring";
  return "valid";
};

const STATUS_STYLES: Record<CellStatus, string> = {
  valid: "bg-green-100 text-green-800 border-green-200",
  expiring: "bg-amber-100 text-amber-800 border-amber-200",
  expired: "bg-red-100 text-red-800 border-red-200",
  none: "bg-gray-100 text-gray-400 border-gray-200",
};

const STATUS_LABEL: Record<CellStatus, string> = {
  valid: "Geldig",
  expiring: "Verloopt binnenkort",
  expired: "Verlopen",
  none: "Niet gedaan",
};

// ─── Component ───────────────────────────────────────────────────────────────

const Training: React.FC = () => {
  const { user, selectedPharmacy } = useAuth();

  const [activeTab, setActiveTab] = useState<"overview" | "records" | "types">("overview");

  const [trainingTypes, setTrainingTypes] = useState<TrainingType[]>([]);
  const [records, setRecords] = useState<TrainingRecord[]>([]);
  const [pharmacyUsers, setPharmacyUsers] = useState<PharmacyUser[]>([]);
  const [loading, setLoading] = useState(true);

  // Type form
  const [showTypeModal, setShowTypeModal] = useState(false);
  const [editingType, setEditingType] = useState<TrainingType | null>(null);
  const [typeForm, setTypeForm] = useState({ name: "", description: "", validity_months: 12, is_mandatory: true });

  // Record form
  const [showRecordModal, setShowRecordModal] = useState(false);
  const [recordForm, setRecordForm] = useState({
    training_type_id: "",
    user_name: "",
    completed_date: format(new Date(), "yyyy-MM-dd"),
    trainer: "",
    notes: "",
  });

  const [confirmDelete, setConfirmDelete] = useState<{ kind: "type" | "record"; id: string } | null>(null);
  const [saving, setSaving] = useState(false);

  // ── Data fetching ──────────────────────────────────────────────────────────

  const fetchAll = async () => {
    if (!selectedPharmacy) return;
    setLoading(true);
    try {
      const [typesRes, recordsRes, usersRes] = await Promise.all([
        supabase
          .from("training_types")
          .select("*")
          .eq("pharmacy_id", selectedPharmacy.id)
          .order("name"),
        supabase
          .from("training_records")
          .select("*, training_type:training_types(name)")
          .eq("pharmacy_id", selectedPharmacy.id)
          .order("completed_date", { ascending: false }),
        supabase
          .from("users")
          .select("id, full_name, role")
          .order("full_name"),
      ]);

      if (typesRes.error) throw typesRes.error;
      if (recordsRes.error) throw recordsRes.error;

      setTrainingTypes(typesRes.data ?? []);
      setRecords(
        (recordsRes.data ?? []).map((r: any) => ({
          ...r,
          training_type_name: r.training_type?.name ?? "—",
        })),
      );
      setPharmacyUsers(usersRes.data ?? []);
    } catch (err) {
      console.error("Error fetching training data:", err);
      toast.error("Fout bij het laden van trainingsgegevens");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedPharmacy) fetchAll();
  }, [selectedPharmacy]);

  // ── Overview matrix ────────────────────────────────────────────────────────

  // Build sorted unique user list from records + pharmacy users
  const matrixUsers = useMemo(() => {
    const fromRecords = records.map((r) => ({ id: r.user_id ?? r.user_name, full_name: r.user_name }));
    const fromUsers = pharmacyUsers.map((u) => ({ id: u.id, full_name: u.full_name }));
    const seen = new Set<string>();
    const merged: { id: string; full_name: string }[] = [];
    [...fromUsers, ...fromRecords].forEach((u) => {
      if (!seen.has(u.full_name)) {
        seen.add(u.full_name);
        merged.push(u);
      }
    });
    return merged.sort((a, b) => a.full_name.localeCompare(b.full_name));
  }, [records, pharmacyUsers]);

  // Most recent record per (user_name, training_type_id)
  const latestRecordMap = useMemo(() => {
    const map: Record<string, TrainingRecord> = {};
    records.forEach((r) => {
      const key = `${r.user_name}::${r.training_type_id}`;
      if (!map[key] || r.completed_date > map[key].completed_date) {
        map[key] = r;
      }
    });
    return map;
  }, [records]);

  // ── Type CRUD ──────────────────────────────────────────────────────────────

  const openAddType = () => {
    setEditingType(null);
    setTypeForm({ name: "", description: "", validity_months: 12, is_mandatory: true });
    setShowTypeModal(true);
  };

  const openEditType = (t: TrainingType) => {
    setEditingType(t);
    setTypeForm({ name: t.name, description: t.description ?? "", validity_months: t.validity_months, is_mandatory: t.is_mandatory });
    setShowTypeModal(true);
  };

  const saveType = async () => {
    if (!selectedPharmacy || !typeForm.name.trim()) return;
    setSaving(true);
    try {
      if (editingType) {
        const { error } = await supabase
          .from("training_types")
          .update({ name: typeForm.name, description: typeForm.description || null, validity_months: typeForm.validity_months, is_mandatory: typeForm.is_mandatory, updated_at: new Date().toISOString() })
          .eq("id", editingType.id);
        if (error) throw error;
        toast.success("Trainingstype bijgewerkt");
      } else {
        const { error } = await supabase
          .from("training_types")
          .insert({ pharmacy_id: selectedPharmacy.id, name: typeForm.name, description: typeForm.description || null, validity_months: typeForm.validity_months, is_mandatory: typeForm.is_mandatory });
        if (error) throw error;
        toast.success("Trainingstype aangemaakt");
      }
      setShowTypeModal(false);
      fetchAll();
    } catch (err: any) {
      toast.error(err.message ?? "Fout bij opslaan");
    } finally {
      setSaving(false);
    }
  };

  const deleteType = async (id: string) => {
    const { error } = await supabase.from("training_types").delete().eq("id", id);
    if (error) { toast.error("Kon trainingstype niet verwijderen"); return; }
    toast.success("Trainingstype verwijderd");
    setConfirmDelete(null);
    fetchAll();
  };

  // ── Record CRUD ────────────────────────────────────────────────────────────

  const openAddRecord = (typeId?: string, userName?: string) => {
    setRecordForm({
      training_type_id: typeId ?? (trainingTypes[0]?.id ?? ""),
      user_name: userName ?? (user?.full_name ?? ""),
      completed_date: format(new Date(), "yyyy-MM-dd"),
      trainer: "",
      notes: "",
    });
    setShowRecordModal(true);
  };

  const saveRecord = async () => {
    if (!selectedPharmacy || !recordForm.training_type_id || !recordForm.user_name.trim() || !recordForm.completed_date) return;
    setSaving(true);
    try {
      const trainingType = trainingTypes.find((t) => t.id === recordForm.training_type_id);
      const completedDate = parseISO(recordForm.completed_date);
      const expiryDate = trainingType ? addMonths(completedDate, trainingType.validity_months) : null;

      const { error } = await supabase.from("training_records").insert({
        pharmacy_id: selectedPharmacy.id,
        training_type_id: recordForm.training_type_id,
        user_name: recordForm.user_name.trim(),
        completed_date: recordForm.completed_date,
        expiry_date: expiryDate ? format(expiryDate, "yyyy-MM-dd") : null,
        trainer: recordForm.trainer || null,
        notes: recordForm.notes || null,
        created_by: user?.id ?? null,
      });
      if (error) throw error;
      toast.success("Training geregistreerd");
      setShowRecordModal(false);
      fetchAll();
    } catch (err: any) {
      toast.error(err.message ?? "Fout bij opslaan");
    } finally {
      setSaving(false);
    }
  };

  const deleteRecord = async (id: string) => {
    const { error } = await supabase.from("training_records").delete().eq("id", id);
    if (error) { toast.error("Kon registratie niet verwijderen"); return; }
    toast.success("Registratie verwijderd");
    setConfirmDelete(null);
    fetchAll();
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="p-6 sm:p-8 flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }

  return (
    <div className="p-6 sm:p-8">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Opleidingsregister</h1>
            <p className="text-gray-600 mt-1">Bijhouden van trainingen en certificeringen per medewerker</p>
          </div>
          <div className="flex items-center space-x-2">
            {activeTab === "records" && (
              <button
                onClick={() => openAddRecord()}
                disabled={trainingTypes.length === 0}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
              >
                <PlusIcon className="h-4 w-4 mr-2" />
                Registreer training
              </button>
            )}
            {activeTab === "types" && (
              <button
                onClick={openAddType}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700"
              >
                <PlusIcon className="h-4 w-4 mr-2" />
                Nieuw type
              </button>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            {(["overview", "records", "types"] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`py-2 px-1 border-b-2 text-sm font-medium ${
                  activeTab === tab
                    ? "border-primary-500 text-primary-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                {tab === "overview" ? "Overzicht" : tab === "records" ? "Registraties" : "Trainingstypes"}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* ── Overview tab ── */}
      {activeTab === "overview" && (
        <div>
          {trainingTypes.length === 0 ? (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
              <AcademicCapIcon className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 text-sm mb-4">Nog geen trainingstypes aangemaakt.</p>
              <button onClick={() => setActiveTab("types")} className="text-sm text-primary-600 hover:underline">
                Maak een trainingstype aan →
              </button>
            </div>
          ) : matrixUsers.length === 0 ? (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center text-gray-500 text-sm">
              Nog geen trainingsregistraties.
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                      Medewerker
                    </th>
                    {trainingTypes.map((t) => (
                      <th key={t.id} className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap max-w-[120px]">
                        <span className="block truncate max-w-[120px]" title={t.name}>{t.name}</span>
                        {t.is_mandatory && <span className="text-red-400 text-xs">verplicht</span>}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {matrixUsers.map((u) => (
                    <tr key={u.full_name} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm font-medium text-gray-900 whitespace-nowrap">
                        {u.full_name}
                      </td>
                      {trainingTypes.map((t) => {
                        const record = latestRecordMap[`${u.full_name}::${t.id}`];
                        const status = cellStatus(record);
                        return (
                          <td key={t.id} className="px-3 py-3 text-center">
                            <button
                              onClick={() => openAddRecord(t.id, u.full_name)}
                              title={record ? `${STATUS_LABEL[status]} — voltooid ${format(parseISO(record.completed_date), "d MMM yyyy", { locale: nl })}${record.expiry_date ? `, verloopt ${format(parseISO(record.expiry_date), "d MMM yyyy", { locale: nl })}` : ""}` : "Klik om training te registreren"}
                              className={`inline-flex items-center justify-center px-2 py-1 rounded-full text-xs font-medium border transition-opacity hover:opacity-80 ${STATUS_STYLES[status]}`}
                            >
                              {status === "valid" && <CheckCircleIcon className="h-3.5 w-3.5 mr-1" />}
                              {status === "expiring" && <ExclamationCircleIcon className="h-3.5 w-3.5 mr-1" />}
                              {status === "expired" && <XCircleIcon className="h-3.5 w-3.5 mr-1" />}
                              {status === "none" ? "—" : record?.expiry_date ? format(parseISO(record.expiry_date), "MM/yy") : "✓"}
                            </button>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Legend */}
          {trainingTypes.length > 0 && (
            <div className="mt-4 flex items-center gap-4 text-xs text-gray-500">
              <span className="flex items-center gap-1"><span className="h-3 w-3 rounded-full bg-green-200 border border-green-300 inline-block" /> Geldig</span>
              <span className="flex items-center gap-1"><span className="h-3 w-3 rounded-full bg-amber-200 border border-amber-300 inline-block" /> Verloopt &lt;60 dagen</span>
              <span className="flex items-center gap-1"><span className="h-3 w-3 rounded-full bg-red-200 border border-red-300 inline-block" /> Verlopen</span>
              <span className="flex items-center gap-1"><span className="h-3 w-3 rounded-full bg-gray-200 border border-gray-300 inline-block" /> Niet geregistreerd</span>
              <span className="ml-2 text-gray-400">Klik op een cel om een training te registreren</span>
            </div>
          )}
        </div>
      )}

      {/* ── Records tab ── */}
      {activeTab === "records" && (
        <div>
          {trainingTypes.length === 0 && (
            <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-md text-sm text-amber-800">
              Maak eerst een trainingstype aan onder het tabblad Trainingstypes.
            </div>
          )}
          {records.length === 0 ? (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center text-gray-500 text-sm">
              Nog geen trainingsregistraties. Klik op "Registreer training" om te beginnen.
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Medewerker</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Training</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Voltooid</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Geldig tot</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Trainer</th>
                    <th className="px-4 py-3" />
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {records.map((r) => {
                    const status = cellStatus(r);
                    return (
                      <tr key={r.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm font-medium text-gray-900 whitespace-nowrap">{r.user_name}</td>
                        <td className="px-4 py-3 text-sm text-gray-700">{r.training_type_name}</td>
                        <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                          {format(parseISO(r.completed_date), "d MMM yyyy", { locale: nl })}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                          {r.expiry_date ? format(parseISO(r.expiry_date), "d MMM yyyy", { locale: nl }) : "—"}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${STATUS_STYLES[status]}`}>
                            {STATUS_LABEL[status]}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">{r.trainer ?? "—"}</td>
                        <td className="px-4 py-3 text-right">
                          <button
                            onClick={() => setConfirmDelete({ kind: "record", id: r.id })}
                            className="text-gray-400 hover:text-red-500"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── Types tab ── */}
      {activeTab === "types" && (
        <div>
          {trainingTypes.length === 0 ? (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
              <AcademicCapIcon className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 text-sm mb-4">Nog geen trainingstypes.</p>
              <button onClick={openAddType} className="inline-flex items-center px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-primary-600 hover:bg-primary-700">
                <PlusIcon className="h-4 w-4 mr-2" />Eerste type aanmaken
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {trainingTypes.map((t) => (
                <div key={t.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-semibold text-gray-900 truncate">{t.name}</h3>
                      {t.description && <p className="text-xs text-gray-500 mt-1 line-clamp-2">{t.description}</p>}
                    </div>
                    <div className="flex items-center gap-1 ml-2 flex-shrink-0">
                      <button onClick={() => openEditType(t)} className="text-gray-400 hover:text-gray-600 p-1">
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      <button onClick={() => setConfirmDelete({ kind: "type", id: t.id })} className="text-gray-400 hover:text-red-500 p-1">
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                  <div className="mt-3 flex items-center gap-3 text-xs text-gray-500">
                    <span className="flex items-center gap-1">
                      <span className="font-medium text-gray-700">{t.validity_months} maanden</span> geldig
                    </span>
                    {t.is_mandatory && (
                      <span className="px-2 py-0.5 rounded-full bg-red-50 text-red-700 border border-red-200 font-medium">verplicht</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Training type modal ── */}
      {showTypeModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setShowTypeModal(false)} />
            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md p-6 z-10">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                {editingType ? "Trainingstype bewerken" : "Nieuw trainingstype"}
              </h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Naam *</label>
                  <input
                    type="text"
                    value={typeForm.name}
                    onChange={(e) => setTypeForm((f) => ({ ...f, name: e.target.value }))}
                    className="block w-full border-gray-300 rounded-md text-sm focus:ring-primary-500 focus:border-primary-500"
                    placeholder="bijv. Hygiëne & HACCP"
                    autoFocus
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Omschrijving</label>
                  <textarea
                    value={typeForm.description}
                    onChange={(e) => setTypeForm((f) => ({ ...f, description: e.target.value }))}
                    rows={2}
                    className="block w-full border-gray-300 rounded-md text-sm focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Geldigheid (maanden)</label>
                  <input
                    type="number"
                    min={1}
                    max={120}
                    value={typeForm.validity_months}
                    onChange={(e) => setTypeForm((f) => ({ ...f, validity_months: parseInt(e.target.value) || 12 }))}
                    className="block w-full border-gray-300 rounded-md text-sm focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="mandatory"
                    checked={typeForm.is_mandatory}
                    onChange={(e) => setTypeForm((f) => ({ ...f, is_mandatory: e.target.checked }))}
                    className="h-4 w-4 text-primary-600 border-gray-300 rounded"
                  />
                  <label htmlFor="mandatory" className="text-sm text-gray-700">Verplichte training</label>
                </div>
              </div>
              <div className="mt-6 flex justify-end gap-3">
                <button onClick={() => setShowTypeModal(false)} className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50">
                  Annuleren
                </button>
                <button
                  onClick={saveType}
                  disabled={saving || !typeForm.name.trim()}
                  className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
                >
                  {saving ? "Opslaan…" : "Opslaan"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Record modal ── */}
      {showRecordModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setShowRecordModal(false)} />
            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md p-6 z-10">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Training registreren</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Training *</label>
                  <select
                    value={recordForm.training_type_id}
                    onChange={(e) => setRecordForm((f) => ({ ...f, training_type_id: e.target.value }))}
                    className="block w-full border-gray-300 rounded-md text-sm focus:ring-primary-500 focus:border-primary-500"
                  >
                    {trainingTypes.map((t) => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Medewerker *</label>
                  <input
                    type="text"
                    list="user-suggestions"
                    value={recordForm.user_name}
                    onChange={(e) => setRecordForm((f) => ({ ...f, user_name: e.target.value }))}
                    className="block w-full border-gray-300 rounded-md text-sm focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Naam medewerker"
                  />
                  <datalist id="user-suggestions">
                    {pharmacyUsers.map((u) => <option key={u.id} value={u.full_name} />)}
                  </datalist>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Voltooiingsdatum *</label>
                  <input
                    type="date"
                    value={recordForm.completed_date}
                    max={format(new Date(), "yyyy-MM-dd")}
                    onChange={(e) => setRecordForm((f) => ({ ...f, completed_date: e.target.value }))}
                    className="block w-full border-gray-300 rounded-md text-sm focus:ring-primary-500 focus:border-primary-500"
                  />
                  {recordForm.training_type_id && recordForm.completed_date && (() => {
                    const t = trainingTypes.find((t) => t.id === recordForm.training_type_id);
                    if (!t) return null;
                    const expiry = addMonths(parseISO(recordForm.completed_date), t.validity_months);
                    return <p className="text-xs text-gray-500 mt-1">Verloopt op {format(expiry, "d MMMM yyyy", { locale: nl })}</p>;
                  })()}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Trainer / opleider</label>
                  <input
                    type="text"
                    value={recordForm.trainer}
                    onChange={(e) => setRecordForm((f) => ({ ...f, trainer: e.target.value }))}
                    className="block w-full border-gray-300 rounded-md text-sm focus:ring-primary-500 focus:border-primary-500"
                    placeholder="bijv. extern opleidingsinstituut"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notities</label>
                  <textarea
                    value={recordForm.notes}
                    onChange={(e) => setRecordForm((f) => ({ ...f, notes: e.target.value }))}
                    rows={2}
                    className="block w-full border-gray-300 rounded-md text-sm focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
              </div>
              <div className="mt-6 flex justify-end gap-3">
                <button onClick={() => setShowRecordModal(false)} className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50">
                  Annuleren
                </button>
                <button
                  onClick={saveRecord}
                  disabled={saving || !recordForm.training_type_id || !recordForm.user_name.trim() || !recordForm.completed_date}
                  className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
                >
                  {saving ? "Opslaan…" : "Registreren"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={confirmDelete !== null}
        title={confirmDelete?.kind === "type" ? "Trainingstype verwijderen" : "Registratie verwijderen"}
        message={
          confirmDelete?.kind === "type"
            ? "Weet je zeker dat je dit trainingstype wilt verwijderen? Alle bijbehorende registraties worden ook verwijderd."
            : "Weet je zeker dat je deze registratie wilt verwijderen?"
        }
        confirmLabel="Verwijderen"
        onConfirm={() => {
          if (!confirmDelete) return;
          if (confirmDelete.kind === "type") deleteType(confirmDelete.id);
          else deleteRecord(confirmDelete.id);
        }}
        onCancel={() => setConfirmDelete(null)}
      />
    </div>
  );
};

export default Training;
